import React, { useCallback, useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

import { Box, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Paper, Select } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import AdCreativeAIAssistant from '~/components/GoogleAds/AdCreativeAIAssistant';

import useAppNavigation from '~/hooks/useAppNavigation';
import useUrlQuery from '~/hooks/useQuery';

import { AdCustomer, AdGroupListItem, getAdGroupsByCampaign, getCustomers } from '~/services/PublicOfferFeedService';

const AD_GROUPS_PAGE_SIZE = 10000;
const AD_GROUPS_PAGE_OFFSET = 0;

export default function AdCreativeAIAssistantContainer() {
  const { push } = useAppNavigation();
  const location = useLocation();
  const urlQuery = useUrlQuery();
  const { enqueueSnackbar } = useSnackbar();

  const selectedCustomerId = urlQuery.get('customerId') || '';
  const selectedCampaignId = urlQuery.get('campaignId') || '';
  const selectedAdGroupId = urlQuery.get('adGroupId') || '';
  const selectedFieldType = (urlQuery.get('fieldType') as 'HEADLINE' | 'DESCRIPTION') || 'HEADLINE';

  const {
    data: customersData,
    isLoading: isLoadingCustomers,
    error: customersError,
  } = useQuery({
    queryKey: ['googleAds', 'customers'],
    queryFn: async () => {
      const response = await getCustomers();
      return response.result;
    },
  });

  const customers: Array<AdCustomer> = useMemo(() => customersData || [], [customersData]);

  const { data: adGroupsData } = useQuery({
    queryKey: ['googleAds', 'adGroups', selectedCustomerId, selectedCampaignId],
    queryFn: async () => {
      if (!selectedCustomerId || !selectedCampaignId) {
        return [];
      }
      const response = await getAdGroupsByCampaign(
        selectedCustomerId,
        selectedCampaignId,
        AD_GROUPS_PAGE_SIZE,
        AD_GROUPS_PAGE_OFFSET,
      );
      return response.result || [];
    },
    enabled: !!selectedCustomerId && !!selectedCampaignId,
  });

  const adGroups: Array<AdGroupListItem> = useMemo(() => adGroupsData || [], [adGroupsData]);

  // Memoized computed values
  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.id === selectedCustomerId),
    [customers, selectedCustomerId],
  );

  const availableCampaigns = useMemo(() => selectedCustomer?.campaigns || [], [selectedCustomer]);

  const selectedAdGroup = useMemo(
    () => adGroups.find((adGroup) => adGroup.ad_group_id === selectedAdGroupId),
    [adGroups, selectedAdGroupId],
  );

  const updateUrlParams = useCallback(
    (updates: { customerId?: string; campaignId?: string; adGroupId?: string; fieldType?: string }) => {
      const newSearchParams = new URLSearchParams(location.search);

      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newSearchParams.set(key, value);
        } else {
          newSearchParams.delete(key);
        }
      });

      const newUrl = `${location.pathname}?${newSearchParams.toString()}`;
      push(newUrl);
    },
    [location.search, location.pathname, push],
  );

  const handleCustomerChange = useCallback(
    (customerId: string) => {
      updateUrlParams({
        customerId,
        campaignId: '',
        adGroupId: '',
      });
    },
    [updateUrlParams],
  );

  const handleCampaignChange = useCallback(
    (campaignId: string) => {
      updateUrlParams({
        campaignId,
        adGroupId: '',
      });
    },
    [updateUrlParams],
  );

  const handleAdGroupChange = useCallback(
    (adGroupId: string) => {
      updateUrlParams({
        adGroupId,
      });
    },
    [updateUrlParams],
  );

  const handleFieldTypeChange = useCallback(
    (fieldType: 'HEADLINE' | 'DESCRIPTION') => {
      updateUrlParams({
        fieldType,
      });
    },
    [updateUrlParams],
  );

  // Auto-select first campaign when customer is selected and no campaign is selected
  useEffect(() => {
    if (selectedCustomerId && !selectedCampaignId && availableCampaigns.length > 0) {
      updateUrlParams({ campaignId: availableCampaigns[0].id });
    }
  }, [selectedCustomerId, selectedCampaignId, availableCampaigns, updateUrlParams]);

  // Auto-select first ad group when campaign is selected and no ad group is selected
  useEffect(() => {
    if (selectedCustomerId && selectedCampaignId && !selectedAdGroupId && adGroups.length > 0) {
      const adGroupIdFromUrl = urlQuery.get('adGroupId');
      const adGroupToSelect =
        adGroupIdFromUrl && adGroups.some((adGroup) => adGroup.ad_group_id === adGroupIdFromUrl)
          ? adGroupIdFromUrl
          : adGroups[0].ad_group_id;
      updateUrlParams({ adGroupId: adGroupToSelect });
    }
  }, [selectedCustomerId, selectedCampaignId, selectedAdGroupId, adGroups, urlQuery, updateUrlParams]);

  // Auto-select first customer
  useEffect(() => {
    if (!selectedCustomerId && customers.length > 0) {
      const customerIdFromUrl = urlQuery.get('customerId');
      const customerToSelect =
        customerIdFromUrl && customers.some((customer) => customer.id === customerIdFromUrl)
          ? customerIdFromUrl
          : customers[0].id;
      updateUrlParams({ customerId: customerToSelect });
    }
  }, [selectedCustomerId, customers, urlQuery, updateUrlParams]);

  useEffect(() => {
    if (customersError) {
      enqueueSnackbar('Failed to fetch customers', { variant: 'error' });
    }
  }, [customersError, enqueueSnackbar]);

  if (isLoadingCustomers) {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress variant="indeterminate" />
      </Box>
    );
  }

  return (
    <Box sx={{ m: 3 }}>
      <Helmet>
        <title>Ad Creative AI Assistant</title>
      </Helmet>

      <PageSubheader title="Ad Creative AI Assistant" />
      <Box sx={{ m: 3 }}>
        <Paper sx={{ padding: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="customer-select-label">Select Customer Account</InputLabel>
                <Select
                  labelId="customer-select-label"
                  id="customer-select"
                  value={selectedCustomerId}
                  label="Select Customer Account"
                  onChange={(e) => handleCustomerChange(e.target.value)}
                >
                  {customers?.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.descriptiveName} ({customer.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth disabled={!selectedCustomerId || availableCampaigns.length === 0}>
                <InputLabel id="campaign-select-label">Select Campaign</InputLabel>
                <Select
                  labelId="campaign-select-label"
                  id="campaign-select"
                  value={selectedCampaignId}
                  label="Select Campaign"
                  onChange={(e) => handleCampaignChange(e.target.value)}
                >
                  {availableCampaigns.length === 0 ? (
                    <MenuItem disabled>
                      {selectedCustomerId ? 'No campaigns available' : 'Select a customer first'}
                    </MenuItem>
                  ) : (
                    availableCampaigns.map((campaign) => (
                      <MenuItem key={campaign.id} value={campaign.id}>
                        {campaign.name} ({campaign.id})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth disabled={!selectedCampaignId || adGroups.length === 0}>
                <InputLabel id="adgroup-select-label">Select Ad Group</InputLabel>
                <Select
                  labelId="adgroup-select-label"
                  id="adgroup-select"
                  value={selectedAdGroupId}
                  label="Select Ad Group"
                  onChange={(e) => handleAdGroupChange(e.target.value)}
                >
                  {adGroups?.map((adGroup) => (
                    <MenuItem key={adGroup.ad_group_id} value={adGroup.ad_group_id}>
                      {adGroup.ad_group_name} ({adGroup.ad_group_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="fieldtype-select-label">Asset Type</InputLabel>
                <Select
                  labelId="fieldtype-select-label"
                  id="fieldtype-select"
                  value={selectedFieldType}
                  label="Asset Type"
                  onChange={(e) => handleFieldTypeChange(e.target.value)}
                >
                  <MenuItem value="HEADLINE">Headline</MenuItem>
                  <MenuItem value="DESCRIPTION">Description</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      <AdCreativeAIAssistant
        campaignId={selectedCampaignId}
        adGroupId={selectedAdGroupId}
        customerId={selectedCustomerId}
        adGroup={selectedAdGroup}
        fieldType={selectedFieldType}
      />
    </Box>
  );
}
